import { useEffect, useRef } from 'react';
import { collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db as firestore } from '../firebase/firebaseConfig';
import { flushQueue } from '../lib/syncQueue';
import { upsertPhoto, deletePhotoLocal, upsertHunt, deleteHuntLocal, getMeta, setMeta, getAllPhotos, getAllHunts } from '../lib/db';

/**
 * Manages Firestore sync for both `photos` and `hunts`:
 *  1. On sign-in / come-online: flush queue first, then attach both snapshot listeners
 *  2. Snapshots keep IDB and React state in sync
 *  3. Calls onSettled() once we know the local hunt list reflects reality —
 *     either signed out (no cloud data possible) or the first hunts snapshot
 *     has processed — so callers can safely decide whether to bootstrap a
 *     default hunt without racing a still-in-flight cloud pull.
 */
export function useSync(user, isOnline, dispatch, onSettled) {
  const unsubPhotosRef = useRef(null);
  const unsubHuntsRef  = useRef(null);

  useEffect(() => {
    // user === undefined means Firebase Auth hasn't resolved a persisted
    // session yet — distinct from user === null (confirmed signed out).
    // Don't settle or touch listeners until we actually know which one it is.
    if (user === undefined) return;

    if (!user || !isOnline) {
      // Tear down listeners if we go offline or sign out. Either way there's
      // no cloud fetch in flight, so it's safe to tell the caller local IDB
      // state is now authoritative.
      if (unsubPhotosRef.current) { unsubPhotosRef.current(); unsubPhotosRef.current = null; }
      if (unsubHuntsRef.current)  { unsubHuntsRef.current();  unsubHuntsRef.current  = null; }
      onSettled?.();
      return;
    }

    // Avoid double-attaching if already listening
    if (unsubPhotosRef.current || unsubHuntsRef.current) return;

    let cancelled = false;

    async function setup() {
      const lastSynced = await getMeta('lastSyncedAt');
      const isFirstDevice = lastSynced === null;

      if (!isFirstDevice) {
        // Flush local queue before listening, so our edits win
        await flushQueue(user.uid);
      }

      if (cancelled) return;

      // Photos listener
      const photosQuery = collection(firestore, 'users', user.uid, 'photos');
      let isInitialPhotosSnapshot = true;

      unsubPhotosRef.current = onSnapshot(photosQuery, async (snapshot) => {
        if (cancelled) return;

        for (const change of snapshot.docChanges()) {
          const data = { ...change.doc.data(), id: change.doc.id };

          if (change.type === 'added' || change.type === 'modified') {
            if (!data.thumbnailDataUrl) {
              const existing = await getPhotoFromIDB(data.id);
              if (existing?.thumbnailDataUrl) data.thumbnailDataUrl = existing.thumbnailDataUrl;
            }
            await upsertPhoto(data);
            dispatch({ type: 'UPSERT_PHOTO', photo: data });
          }

          if (change.type === 'removed') {
            await deletePhotoLocal(data.id);
            dispatch({ type: 'DELETE_PHOTO', id: data.id });
          }
        }

        if (isInitialPhotosSnapshot) {
          isInitialPhotosSnapshot = false;
          if (isFirstDevice) {
            const allPhotos = await getAllPhotos();
            dispatch({ type: 'SET_PHOTOS', photos: allPhotos });
          }
        }
      }, (err) => {
        console.error('Firestore photos snapshot error:', err);
      });

      // Hunts listener — independent subscription, same shape as photos
      const huntsQuery = collection(firestore, 'users', user.uid, 'hunts');
      let isInitialHuntsSnapshot = true;

      const cloudHuntIds = new Set();

      unsubHuntsRef.current = onSnapshot(huntsQuery, async (snapshot) => {
        if (cancelled) return;

        for (const change of snapshot.docChanges()) {
          const data = { ...change.doc.data(), id: change.doc.id };

          if (change.type === 'added' || change.type === 'modified') {
            cloudHuntIds.add(data.id);
            await upsertHunt(data);
            dispatch({ type: 'UPSERT_HUNT', hunt: data });
          }

          if (change.type === 'removed') {
            cloudHuntIds.delete(data.id);
            await deleteHuntLocal(data.id);
            dispatch({ type: 'DELETE_HUNT', id: data.id });
          }
        }

        if (isInitialHuntsSnapshot) {
          isInitialHuntsSnapshot = false;
          const allLocalHunts = await getAllHunts();
          if (isFirstDevice) {
            dispatch({ type: 'SET_HUNTS', hunts: allLocalHunts });
          }

          // Repair pass: a hunt that exists locally but never made it into this
          // initial cloud snapshot was saved to IDB but never actually written
          // to Firestore — a real historical case being an auth-state race that
          // made the app briefly think it was signed out at the moment a hunt
          // was created. Push those back up so every device converges.
          for (const hunt of allLocalHunts) {
            if (cloudHuntIds.has(hunt.id)) continue;
            try {
              await setDoc(doc(firestore, 'users', user.uid, 'hunts', hunt.id), hunt);
            } catch (err) {
              console.error('Hunt repair-sync failed:', hunt.id, err);
            }
          }

          await setMeta('lastSyncedAt', new Date().toISOString());
          onSettled?.();
        }
      }, (err) => {
        console.error('Firestore hunts snapshot error:', err);
      });
    }

    setup();

    return () => {
      cancelled = true;
      if (unsubPhotosRef.current) { unsubPhotosRef.current(); unsubPhotosRef.current = null; }
      if (unsubHuntsRef.current)  { unsubHuntsRef.current();  unsubHuntsRef.current  = null; }
    };
  }, [user, isOnline, dispatch, onSettled]);
}

// Helper: read a single photo from IDB without importing everything
async function getPhotoFromIDB(id) {
  const { getAllPhotos } = await import('../lib/db');
  const photos = await getAllPhotos();
  return photos.find(p => p.id === id) ?? null;
}
